import React from "react";
import { motion } from "framer-motion";
import SEO from "../components/SEO";
import StructuredData, {
  createBreadcrumbSchema,
} from "../components/StructuredData";
import { seoData } from "../utils/seo";

const Privacy = () => {
  const privacySeo = seoData.privacy;
  const structuredData = [
    createBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Privacy Policy", url: privacySeo.url },
    ]),
  ];

  return (
    <div className="min-h-screen pt-24 pb-20 bg-gray-50">
      <SEO
        title={privacySeo.title}
        description={privacySeo.description}
        keywords={privacySeo.keywords}
        url={privacySeo.url}
      />
      <StructuredData data={structuredData} />
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8 md:p-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Estabild - Privacy Policy
          </h1>

          <p className="text-sm text-gray-500 mb-8">
            Effective date: November 25th, 2019
            <br />
            Revised December 1st, 2021
          </p>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            {/* Section 1 */}
            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                1. Introduction
              </h3>
              <div className="space-y-4">
                <p>
                  Welcome to <strong>Estabild AB</strong>.
                </p>
                <p>
                  Estabild AB ("us", "we", or "our") operates www.estabild.com
                  (hereinafter referred to as "<strong>Service</strong>").
                </p>
                <p>
                  Our Privacy Policy governs your visit to www.estabild.com, and
                  explains how we collect, safeguard and disclose information
                  that results from your use of our Service.
                </p>
                <p>
                  We use your data to provide and improve Service. By using
                  Service, you agree to the collection and use of information in
                  accordance with this policy. Unless otherwise defined in this
                  Privacy Policy, the terms used in this Privacy Policy have the
                  same meanings as in our Terms and Conditions.
                </p>
                <p>
                  Our Terms and Conditions ("<strong>Terms</strong>") govern all
                  use of our Service and together with the Privacy Policy
                  constitutes your agreement with us ("
                  <strong>agreement</strong>").
                </p>
              </div>
            </section>

            {/* Section 2 */}
            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                2. Definitions
              </h3>
              <div className="space-y-3">
                <p>
                  <strong>SERVICE</strong> means the www.estabild.com website
                  operated by Estabild AB.
                </p>
                <p>
                  <strong>PERSONAL DATA</strong> means data about a living
                  individual who can be identified from those data (or from
                  those and other information either in our possession or likely
                  to come into our possession).
                </p>
                <p>
                  <strong>USAGE DATA</strong> is data collected automatically
                  either generated by the use of Service or from Service
                  infrastructure itself (for example, the duration of a page
                  visit).
                </p>
                <p>
                  <strong>COOKIES</strong> are small files stored on your device
                  (computer or mobile device).
                </p>
                <p>
                  <strong>DATA CONTROLLER</strong> means a natural or legal
                  person who (either alone or jointly or in common with other
                  persons) determines the purposes for which and the manner in
                  which any personal data are, or are to be, processed. For the
                  purpose of this Privacy Policy, we are a Data Controller of
                  your data.
                </p>
                <p>
                  <strong>DATA PROCESSORS (OR SERVICE PROVIDERS)</strong> means
                  any natural or legal person who processes the data on behalf
                  of the Data Controller. We may use the services of various
                  Service Providers in order to process your data more
                  effectively.
                </p>
                <p>
                  <strong>DATA SUBJECT</strong> is any living individual who is
                  the subject of Personal Data.
                </p>
                <p>
                  <strong>THE USER</strong> is the individual using our Service.
                  The User corresponds to the Data Subject, who is the subject
                  of Personal Data.
                </p>
              </div>
            </section>

            {/* Section 3 */}
            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                3. Information Collection and Use
              </h3>
              <p>
                We collect several different types of information for various
                purposes to provide and improve our Service to you.
              </p>
            </section>

            {/* Section 4 */}
            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                4. Types of Data Collected
              </h3>
              <div className="space-y-6">
                <div>
                  <p className="font-semibold mb-3">Personal Data</p>
                  <div className="space-y-4">
                    <p>
                      While using our Service, we may ask you to provide us with
                      certain personally identifiable information that can be
                      used to contact or identify you ("
                      <strong>Personal Data</strong>"). Personally identifiable
                      information may include, but is not limited to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Email address</li>
                      <li>First name and last name</li>
                      <li>Phone number</li>
                      <li>Address, State, Province, ZIP/Postal code, City</li>
                      <li>Cookies and Usage Data</li>
                    </ul>
                    <p>
                      We may use your Personal Data to contact you with
                      newsletters, marketing or promotional materials and other
                      information that may be of interest to you. You may opt
                      out of receiving any, or all, of these communications from
                      us by following the unsubscribe link or by emailing at{" "}
                      <a
                        href="mailto:info@estabild.com?subject=Privacy%20Policy"
                        className="text-primary-600 hover:text-primary-700"
                      >
                        info@estabild.com
                      </a>
                      .
                    </p>
                  </div>
                </div>

                <div>
                  <p className="font-semibold mb-3">Usage Data</p>
                  <div className="space-y-4">
                    <p>
                      We may also collect information that your browser sends
                      whenever you visit our Service or when you access Service
                      by or through a mobile device ("
                      <strong>Usage Data</strong>
                      ").
                    </p>
                    <p>
                      This Usage Data may include information such as your
                      computer's Internet Protocol address (e.g. IP address),
                      browser type, browser version, the pages of our Service
                      that you visit, the time and date of your visit, the time
                      spent on those pages, unique device identifiers and other
                      diagnostic data.
                    </p>
                    <p>
                      When you access Service with a mobile device, this Usage
                      Data may include information such as the type of mobile
                      device you use, your mobile device unique ID, the IP
                      address of your mobile device, your mobile operating
                      system, the type of mobile Internet browser you use,
                      unique device identifiers and other diagnostic data.
                    </p>
                  </div>
                </div>

                <div>
                  <p className="font-semibold mb-3">Location Data</p>
                  <div className="space-y-4">
                    <p>
                      We may use and store information about your location if
                      you give us permission to do so ("
                      <strong>Location Data</strong>
                      "). We use this data to provide features of our Service,
                      to improve and customize our Service.
                    </p>
                    <p>
                      You can enable or disable location services when you use
                      our Service at any time by way of your device settings.
                    </p>
                  </div>
                </div>

                <div>
                  <p className="font-semibold mb-3">Tracking Cookies Data</p>
                  <div className="space-y-4">
                    <p>
                      We use cookies and similar tracking technologies to track
                      the activity on our Service and we hold certain
                      information.
                    </p>
                    <p>
                      Cookies are files with a small amount of data which may
                      include an anonymous unique identifier. Cookies are sent
                      to your browser from a website and stored on your device.
                      Other tracking technologies are also used such as beacons,
                      tags and scripts to collect and track information and to
                      improve and analyze our Service.
                    </p>
                    <p>
                      You can instruct your browser to refuse all cookies or to
                      indicate when a cookie is being sent. However, if you do
                      not accept cookies, you may not be able to use some
                      portions of our Service.
                    </p>
                    <p>Examples of Cookies we use:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        <strong>Session Cookies:</strong> We use Session Cookies
                        to operate our Service.
                      </li>
                      <li>
                        <strong>Preference Cookies:</strong> We use Preference
                        Cookies to remember your preferences and various
                        settings.
                      </li>
                      <li>
                        <strong>Security Cookies:</strong> We use Security
                        Cookies for security purposes.
                      </li>
                      <li>
                        <strong>Advertising Cookies:</strong> Advertising
                        Cookies are used to serve you with advertisements that
                        may be relevant to you and your interests.
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 5 */}
            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                5. Use of Data
              </h3>
              <p className="mb-4">
                Estabild AB uses the collected data for various purposes:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>to provide and maintain our Service</li>
                <li>to notify you about changes to our Service</li>
                <li>
                  to allow you to participate in interactive features of our
                  Service when you choose to do so
                </li>
                <li>to provide customer support</li>
                <li>
                  to gather analysis or valuable information so that we can
                  improve our Service
                </li>
                <li>to monitor the usage of our Service</li>
                <li>to detect, prevent and address technical issues</li>
                <li>to fulfill any other purpose for which you provide it</li>
                <li>
                  to carry out our obligations and enforce our rights arising
                  from any contracts entered into between you and us, including
                  for billing and collection
                </li>
                <li>
                  to provide you with notices about your account and/or
                  subscription, including expiration and renewal notices,
                  email-instructions, etc.
                </li>
                <li>
                  to provide you with news, special offers and general
                  information about other goods, services and events which we
                  offer that are similar to those that you have already
                  purchased or enquired about unless you have opted not to
                  receive such information
                </li>
                <li>
                  in any other way we may describe when you provide the
                  information
                </li>
                <li>for any other purpose with your consent</li>
              </ul>
            </section>

            {/* Section 6 */}
            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                6. Retention of Data
              </h3>
              <div className="space-y-4">
                <p>
                  We will retain your Personal Data only for as long as is
                  necessary for the purposes set out in this Privacy Policy. We
                  will retain and use your Personal Data to the extent necessary
                  to comply with our legal obligations (for example, if we are
                  required to retain your data to comply with applicable laws),
                  resolve disputes, and enforce our legal agreements and
                  policies.
                </p>
                <p>
                  We will also retain Usage Data for internal analysis purposes.
                  Usage Data is generally retained for a shorter period, except
                  when this data is used to strengthen the security or to
                  improve the functionality of our Service, or we are legally
                  obligated to retain this data for longer time periods.
                </p>
              </div>
            </section>

            {/* Section 7 */}
            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                7. Transfer of Data
              </h3>
              <div className="space-y-4">
                <p>
                  Your information, including Personal Data, may be transferred
                  to – and maintained on – computers located outside of your
                  state, province, country or other governmental jurisdiction
                  where the data protection laws may differ from those of your
                  jurisdiction.
                </p>
                <p>
                  If you are located outside United States and choose to provide
                  information to us, please note that we transfer the data,
                  including Personal Data, to United States and process it
                  there.
                </p>
                <p>
                  Your consent to this Privacy Policy followed by your
                  submission of such information represents your agreement to
                  that transfer.
                </p>
                <p>
                  Estabild AB will take all the steps reasonably necessary to
                  ensure that your data is treated securely and in accordance
                  with this Privacy Policy and no transfer of your Personal Data
                  will take place to an organisation or a country unless there
                  are adequate controls in place including the security of your
                  data and other personal information.
                </p>
              </div>
            </section>

            {/* Section 8 */}
            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                8. Disclosure of Data
              </h3>
              <div className="space-y-4">
                <p>
                  We may disclose personal information that we collect, or you
                  provide:
                </p>
                <div>
                  <p className="font-semibold mb-2">
                    a) Disclosure for Law Enforcement.
                  </p>
                  <p>
                    Under certain circumstances, we may be required to disclose
                    your Personal Data if required to do so by law or in
                    response to valid requests by public authorities.
                  </p>
                </div>
                <div>
                  <p className="font-semibold mb-2">b) Business Transaction.</p>
                  <p>
                    If we or our subsidiaries are involved in a merger,
                    acquisition or asset sale, your Personal Data may be
                    transferred.
                  </p>
                </div>
                <div>
                  <p className="font-semibold mb-2">
                    c) Other cases. We may disclose your information also:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>To our subsidiaries and affiliates</li>
                    <li>
                      to contractors, service providers, and other third parties
                      we use to support our business
                    </li>
                    <li>to fulfill the purpose for which you provide it</li>
                    <li>
                      for the purpose of including your company's logo on our
                      website
                    </li>
                    <li>
                      for any other purpose disclosed by us when you provide the
                      information
                    </li>
                    <li>with your consent in any other cases</li>
                    <li>
                      if we believe disclosure is necessary or appropriate to
                      protect the rights, property, or safety of the Company,
                      our customers, or others
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 9 */}
            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                9. Security of Data
              </h3>
              <p>
                The security of your data is important to us but remember that
                no method of transmission over the Internet or method of
                electronic storage is 100% secure. While we strive to use
                commercially acceptable means to protect your Personal Data, we
                cannot guarantee its absolute security.
              </p>
            </section>

            {/* Section 10 */}
            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                10. Your Data Protection Rights Under General Data Protection
                Regulation (GDPR)
              </h3>
              <div className="space-y-4">
                <p>
                  If you are a resident of the European Union (EU) and European
                  Economic Area (EEA), you have certain data protection rights,
                  covered by GDPR. You can find more details{" "}
                  <a
                    href="https://eur-lex.europa.eu/eli/reg/2016/679/oj"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700"
                  >
                    here
                  </a>
                  .
                </p>
                <p>
                  We aim to take reasonable steps to allow you to correct,
                  amend, delete, or limit the use of your Personal Data.
                </p>
                <p>
                  If you wish to be informed what Personal Data we hold about
                  you and if you want it to be removed from our systems, please
                  email us at info@estabild.com.
                </p>
                <p>
                  In certain circumstances, you have the following data
                  protection rights:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    the right to access, update or to delete the information we
                    have on you
                  </li>
                  <li>
                    the right of rectification. You have the right to have your
                    information rectified if that information is inaccurate or
                    incomplete
                  </li>
                  <li>
                    the right to object. You have the right to object to our
                    processing of your Personal Data
                  </li>
                  <li>
                    the right of restriction. You have the right to request that
                    we restrict the processing of your personal information
                  </li>
                  <li>
                    the right to data portability. You have the right to be
                    provided with a copy of your Personal Data in a structured,
                    machine-readable and commonly used format
                  </li>
                  <li>
                    the right to withdraw consent. You also have the right to
                    withdraw your consent at any time where we rely on your
                    consent to process your personal information
                  </li>
                </ul>
                <p>
                  Please note that we may ask you to verify your identity before
                  responding to such requests.
                </p>
                <p>
                  Please note, we may not able to provide Service without some
                  necessary data. You have the right to complain to a Data
                  Protection Authority about our collection and use of your
                  Personal Data. For more information, please contact your local
                  data protection authority in the European Economic Area (EEA).
                </p>
              </div>
            </section>

            {/* Section 11 */}
            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                11. Your Data Protection Rights under the California Privacy
                Protection Act (CalOPPA)
              </h3>
              <div className="space-y-4">
                <p>
                  CalOPPA is the first state law in the nation to require
                  commercial websites and online services to post a privacy
                  policy. The law's reach stretches well beyond California to
                  require a person or company in the United States (and
                  conceivable the world) that operates websites collecting
                  personally identifiable information from California consumers
                  to post a conspicuous privacy policy on its website stating
                  exactly the information being collected and those individuals
                  with whom it is being shared, and to comply with this policy.
                  See more details{" "}
                  <a
                    href="https://consumercal.org/about-cfc/cfc-education-foundation/california-online-privacy-protection-act-caloppa-3/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700"
                  >
                    here
                  </a>
                  .
                </p>
                <p>According to CalOPPA we agree to the following:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>users can visit our site anonymously</li>
                  <li>
                    our Privacy Policy link includes the word "Privacy", and can
                    easily be found on the page specified above on the home page
                    of our website
                  </li>
                  <li>
                    users will be notified of any privacy policy changes on our
                    Privacy Policy Page
                  </li>
                  <li>
                    users are able to change their personal information by
                    emailing us at info@estabild.com
                  </li>
                </ul>
                <p className="font-semibold">
                  Our Policy on "Do Not Track" Signals:
                </p>
                <p>
                  We honour Do Not Track signals and do not track, plant
                  cookies, or use advertising when a Do Not Track browser
                  mechanism is in place. Do Not Track is a preference you can
                  set in your web browser to inform websites that you do not
                  want to be tracked.
                </p>
                <p>
                  You can enable or disable Do Not Track by visiting the
                  Preferences or Settings page of your web browser.
                </p>
              </div>
            </section>

            {/* Section 13 - Analytics */}
            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                13. Analytics
              </h3>
              <div className="space-y-4">
                <p>
                  We may use third-party Service Providers to monitor and
                  analyze the use of our Service.
                </p>
                <div>
                  <p className="font-semibold">Smartlook</p>
                  <p>
                    Smartlook is an analytics tool for web and mobile that
                    records user sessions, we use it to analyze general user
                    behaviour and statistics. You can read the Smartlook's{" "}
                    <a
                      href="https://help.smartlook.com/en/articles/3244452-privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700"
                    >
                      privacy policy
                    </a>{" "}
                    and{" "}
                    <a
                      href="https://help.smartlook.com/en/articles/3244454-data-security"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700"
                    >
                      data privacy
                    </a>
                    .
                  </p>
                </div>
                <div>
                  <p className="font-semibold">Google Analytics</p>
                  <p>
                    Google Analytics is a web analytics service offered by
                    Google that tracks and reports website traffic. Google uses
                    the data collected to track and monitor the use of our
                    Service. This data is shared with other Google services.
                    Google may use the collected data to contextualise and
                    personalise the ads of its own advertising network. For more
                    information on the privacy practices of Google, please visit
                    the{" "}
                    <a
                      href="https://policies.google.com/privacy?hl=en"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700"
                    >
                      Google Privacy Terms web page
                    </a>
                    . We also encourage you to review the{" "}
                    <a
                      href="https://support.google.com/analytics/answer/6004245"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700"
                    >
                      Google's policy
                    </a>{" "}
                    for safeguarding your data.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 18 */}
            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                18. Children's Privacy
              </h3>
              <div className="space-y-4">
                <p>
                  Our Services are not intended for use by children under the
                  age of 18 ("<strong>Child</strong>" or "
                  <strong>Children</strong>
                  ").
                </p>
                <p>
                  We do not knowingly collect personally identifiable
                  information from Children under 18. If you become aware that a
                  Child has provided us with Personal Data, please contact us.
                </p>
                <p>
                  If we become aware that we have collected Personal Data from
                  Children without verification of parental consent, we take
                  steps to remove that information from our servers.
                </p>
              </div>
            </section>

            {/* Section 19 */}
            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                19. Changes to This Privacy Policy
              </h3>
              <div className="space-y-4">
                <p>
                  We may update our Privacy Policy from time to time. We will
                  notify you of any changes by posting the new Privacy Policy on
                  this page.
                </p>
                <p>
                  We will let you know via email and/or a prominent notice on
                  our Service, prior to the change becoming effective and update
                  "effective date" at the top of this Privacy Policy.
                </p>
                <p>
                  You are advised to review this Privacy Policy periodically for
                  any changes. Changes to this Privacy Policy are effective when
                  they are posted on this page.
                </p>
              </div>
            </section>

            {/* Section 20 */}
            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                20. Contact Us
              </h3>
              <p className="mb-4">
                If you have any questions about this Privacy Policy, please
                contact us:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  By email:{" "}
                  <a
                    href="mailto:info@estabild.com?subject=Privacy%20Policy"
                    className="text-primary-600 hover:text-primary-700"
                  >
                    info@estabild.com
                  </a>
                  .
                </li>
                <li>
                  By visiting this page on our website:{" "}
                  <a
                    href="/about"
                    className="text-primary-600 hover:text-primary-700"
                  >
                    estabild.com/about
                  </a>
                  .
                </li>
                <li>By phone number: +46-81-213-7994.</li>
                <li>By mail: Karlbergsvägen 44, 113 26 Stockholm, Sweden.</li>
              </ul>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Privacy;
